import clsx from 'clsx';
import { Building2, Search, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button, Field, Input, Select, Spinner } from '../../components/ui';
import { errorMessage, get } from '../../lib/api';
import { ContactView, ExternalService, TicketSummary } from '../../lib/types';

export type Customer = { id: string; name: string };

/** Cliente (empresa) do Agidesk: obrigatório para criar chamado. Sugestões vêm da busca do contato. */
export function CustomerPicker({ contact, value, onChange }: { contact: ContactView; value: Customer | null; onChange: (c: Customer | null) => void }) {
  const [filter, setFilter] = useState('');
  const [results, setResults] = useState<Customer[] | null>(null);
  const [loading, setLoading] = useState(false);
  // Só abre a busca quando o operador pede "trocar" (ou quando ainda não há cliente).
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const q = filter.trim();
    if (q.length < 2) {
      setResults(null);
      return;
    }
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      get<Customer[]>(`/agidesk/customers?filter=${encodeURIComponent(q)}`)
        .then((r) => alive && setResults(r))
        .catch(() => alive && setResults([]))
        .finally(() => alive && setLoading(false));
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [filter]);

  // Sem cliente definido, mas a busca do contato achou exatamente uma empresa: já vem preenchida.
  const autoPick = !value ? uniqueSuggestion(contact) : null;
  useEffect(() => {
    if (autoPick) onChange(autoPick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPick?.id]);

  const suggestions = suggestedCustomers(contact).filter((c, i, all) => all.findIndex((x) => x.id === c.id) === i);

  if (value && !editing) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
        <Building2 className="size-4 text-fg-soft" />
        <span className="flex-1">
          {value.name} <span className="text-xs text-fg-soft">(código {value.id})</span>
        </span>
        <button className="text-xs text-accent hover:underline" onClick={() => setEditing(true)}>
          trocar
        </button>
      </div>
    );
  }

  const pick = (c: Customer) => {
    onChange(c);
    setEditing(false);
    setFilter('');
  };

  return (
    <div>
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-fg-soft" />
        <Input autoFocus value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Buscar cliente no Agidesk (nome ou código)" className="pl-8" />
      </div>
      {suggestions.length > 0 && !results && (
        <div className="mt-2 text-xs text-fg-soft">
          Sugestões:{' '}
          {suggestions.map((c) => (
            <button key={c.id} className="mr-2 text-accent hover:underline" onClick={() => pick(c)}>
              {c.name}
            </button>
          ))}
        </div>
      )}
      {loading && <Spinner className="mt-2 size-4" />}
      {results && (
        <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-border">
          {results.length === 0 && <div className="px-3 py-2 text-sm text-fg-soft">Nenhum cliente encontrado.</div>}
          {results.map((c) => (
            <button key={c.id} className="block w-full px-3 py-2 text-left text-sm hover:bg-muted" onClick={() => pick(c)}>
              {c.name} <span className="text-xs text-fg-soft">({c.id})</span>
            </button>
          ))}
        </div>
      )}
      {value && (
        <button className="mt-1 text-xs text-fg-soft hover:underline" onClick={() => setEditing(false)}>
          manter {value.name}
        </button>
      )}
    </div>
  );
}

/** Empresas sugeridas pelo Agidesk (do contato e dos contatos com o mesmo telefone); o contato pode não ter sugestões. */
const suggestedCustomers = (contact: ContactView): Customer[] => {
  const s = contact.agidesk.suggestions;
  return s ? [...s.customers, ...s.phoneMatches.flatMap((p) => p.customers)] : [];
};

function uniqueSuggestion(contact: ContactView): Customer | null {
  const all = suggestedCustomers(contact);
  const ids = new Set(all.map((c) => c.id));
  return ids.size === 1 ? all[0] : null;
}

export type TicketChoiceValue =
  | { mode: 'reuse'; ticketId: string }
  | { mode: 'create'; serviceId: string; title: string }
  | { mode: 'deferred' };

/** Reutilizar um chamado aberto, criar um novo (serviço + assunto) ou decidir depois. */
export function TicketChoice({
  value,
  onChange,
  openTickets,
  services,
  agideskAvailable,
  defaultTitle = '',
  allowDeferred = true,
  onSuggest,
}: {
  defaultTitle?: string;
  allowDeferred?: boolean;
  /** IA ligada: sugere serviço e assunto pela conversa e pelo sistema do cliente. */
  onSuggest?: () => Promise<{ serviceId: string | null; title: string; system: string | null }>;
  value: TicketChoiceValue;
  onChange: (v: TicketChoiceValue) => void;
  openTickets: TicketSummary[] | null;
  services: ExternalService[] | null;
  agideskAvailable: boolean;
}) {
  // Rascunho de "criar novo": preservado ao alternar entre as opções.
  const [draft, setDraft] = useState({ serviceId: '', title: defaultTitle });
  useEffect(() => {
    if (value.mode === 'create') setDraft({ serviceId: value.serviceId, title: value.title });
  }, [value]);
  const { serviceId, title } = value.mode === 'create' ? value : draft;
  const option = (mode: TicketChoiceValue['mode'], label: string, disabled = false) => (
    <label className={clsx('flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm', value.mode === mode ? 'border-accent bg-accent/5' : 'border-border', disabled && 'cursor-not-allowed opacity-50')}>
      <input
        type="radio"
        disabled={disabled}
        checked={value.mode === mode}
        onChange={() =>
          onChange(mode === 'reuse' ? { mode, ticketId: openTickets?.[0]?.id ?? '' } : mode === 'create' ? { mode, serviceId, title } : { mode })
        }
      />
      {label}
    </label>
  );
  const sorted = [...(services ?? [])].sort((a, b) => (a.fullName ?? a.name).localeCompare(b.fullName ?? b.name, 'pt-BR'));
  const [suggesting, setSuggesting] = useState(false);
  const [suggestNote, setSuggestNote] = useState<string | null>(null);
  const suggest = async () => {
    if (!onSuggest) return;
    setSuggesting(true);
    setSuggestNote(null);
    try {
      const s = await onSuggest();
      onChange({ mode: 'create', serviceId: s.serviceId ?? serviceId, title: s.title || title });
      setSuggestNote(
        [s.system ? `Sistema do cliente: ${s.system} (tipo no Agidesk)` : 'Sistema do cliente não informado no Agidesk', s.serviceId ? null : 'escolha o serviço'].filter(Boolean).join('; '),
      );
    } catch (err) {
      setSuggestNote(errorMessage(err));
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <div>
      {!agideskAvailable && <p className="mb-2 rounded-md bg-warn/10 px-3 py-2 text-sm text-warn">Agidesk indisponível: use "decidir depois". O registro sincroniza quando ele voltar.</p>}
      <div className={clsx('grid gap-2', allowDeferred ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
        {option('reuse', `Vincular chamado aberto (${openTickets?.length ?? 0})`, !openTickets?.length)}
        {option('create', 'Abrir chamado novo', !services?.length)}
        {allowDeferred && option('deferred', 'Decidir depois')}
      </div>

      {value.mode === 'reuse' && (
        <div className="mt-3 max-h-48 overflow-y-auto rounded-md border border-border">
          {(openTickets ?? []).map((t) => (
            <label key={t.id} className={clsx('flex cursor-pointer items-start gap-2 border-b border-border px-3 py-2 last:border-0', value.ticketId === t.id && 'bg-accent/5')}>
              <input type="radio" checked={value.ticketId === t.id} onChange={() => onChange({ mode: 'reuse', ticketId: t.id })} className="mt-1" />
              <span className="text-sm">
                <b>{t.code}</b> {t.title}
                {t.serviceName && <span className="block text-xs text-fg-soft">{t.serviceName}</span>}
              </span>
            </label>
          ))}
        </div>
      )}

      {value.mode === 'create' && (
        <div className="mt-3">
          {onSuggest && (
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Button size="sm" variant="ghost" loading={suggesting} onClick={() => void suggest()} title="A IA lê a conversa e o tipo do cliente no Agidesk e sugere o serviço e o assunto">
                <Sparkles className="size-3.5" /> Sugerir serviço e assunto com IA
              </Button>
              {suggestNote && <span className="text-[11px] text-fg-soft">{suggestNote}</span>}
            </div>
          )}
          <Field label="Serviço">
            <Select
              value={serviceId}
              onChange={(e) => onChange({ mode: 'create', serviceId: e.target.value, title })}
            >
              <option value="">Escolha o serviço…</option>
              {sorted.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.fullName ?? s.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Assunto do chamado">
            <Input
              value={title}
              maxLength={256}
              onChange={(e) => onChange({ mode: 'create', serviceId, title: e.target.value })}
              placeholder="Ex.: Dúvida sobre emissão de nota"
            />
          </Field>
        </div>
      )}

      {value.mode === 'deferred' && <p className="mt-3 text-sm text-fg-soft">O atendimento segue normalmente; o chamado precisa ser definido antes de o registro ir para o Agidesk.</p>}
    </div>
  );
}

/** Corpo da decisão para a API (TicketDecisionDto). */
export function decisionBody(v: TicketChoiceValue) {
  if (v.mode === 'reuse') return { mode: 'reuse', ticketId: v.ticketId };
  if (v.mode === 'create') return { mode: 'create', serviceId: v.serviceId, title: v.title.trim() };
  return { mode: 'deferred' };
}

export function decisionError(v: TicketChoiceValue, customer: Customer | null): string | null {
  if (v.mode === 'reuse' && !v.ticketId) return 'Escolha o chamado aberto';
  if (v.mode === 'create') {
    if (!customer) return 'Defina o cliente (empresa) do contato para criar o chamado';
    if (!v.serviceId) return 'Escolha o serviço';
    if (!v.title.trim()) return 'Informe o assunto do chamado';
  }
  return null;
}
